import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { FaCreditCard, FaCalendar, FaCheck, FaTimes, FaSpinner, FaShieldAlt } from 'react-icons/fa';

interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  cancel_at_period_end: boolean;
  items: Array<{
    price: {
      product: string;
      unit_amount: number;
      currency: string;
      recurring: {
        interval: string;
      };
    };
  }>;
}

const SubscriptionManager: React.FC = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canceling, setCanceling] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSubscriptions(data.subscriptions);
      } else {
        setError(data.error || 'Failed to fetch subscriptions');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async (subscriptionId: string) => {
    setCanceling(subscriptionId);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe/subscriptions/${subscriptionId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh subscriptions
        await fetchSubscriptions();
      } else {
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setCanceling(null);
    }
  };

  const reactivateSubscription = async (subscriptionId: string) => {
    setReactivating(subscriptionId);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/stripe/subscriptions/${subscriptionId}/reactivate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh subscriptions
        await fetchSubscriptions();
      } else {
        setError(data.error || 'Failed to reactivate subscription');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setReactivating(null);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const getStatusBadge = (status: string, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return (
        <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
          Canceling
        </span>
      );
    }

    switch (status) {
      case 'active':
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
            Active
          </span>
        );
      case 'canceled':
        return (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
            Canceled
          </span>
        );
      case 'past_due':
        return (
          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
            Past Due
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <FaSpinner className="animate-spin text-persian-sky text-xl" />
        <span className="ml-2 text-persian-text">Loading subscriptions...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-persian-ruby/10 border border-persian-ruby/20 rounded-lg p-4">
        <p className="text-persian-ruby">{error}</p>
        <button
          onClick={fetchSubscriptions}
          className="mt-2 text-persian-ruby hover:text-persian-rose underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="text-center py-8">
        <FaCreditCard className="text-persian-muted text-4xl mx-auto mb-4" />
        <h3 className="text-lg font-medium text-persian-text mb-2">No Active Subscriptions</h3>
        <p className="text-persian-text-alt mb-4">
          You don't have any active subscriptions at the moment.
        </p>
        <a
          href="/pricing"
          className="inline-flex items-center px-4 py-2 bg-persian-sky text-persian-bg rounded-lg hover:bg-persian-turquoise transition-colors"
        >
          View Plans
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cancel Anytime Policy Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white">
        <div className="flex items-center">
          <FaShieldAlt className="text-white text-xl mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold mb-1">
              Cancel Anytime Policy
            </h3>
            <p className="text-green-100 text-xs">
              You can cancel your subscription at any time. Your access will continue until the end of your current billing period.
            </p>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-persian-text">Your Subscriptions</h2>
      
      {subscriptions.map((subscription) => (
        <div
          key={subscription.id}
          className="bg-persian-card border border-persian-card-alt rounded-lg p-6 shadow-sm"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-persian-text">
                {subscription.items[0]?.price.recurring.interval === 'month' ? 'Monthly' : 'Yearly'} Plan
              </h3>
              <p className="text-persian-text-alt">
                {formatAmount(
                  subscription.items[0]?.price.unit_amount || 0,
                  subscription.items[0]?.price.currency || 'usd'
                )} per {subscription.items[0]?.price.recurring.interval}
              </p>
            </div>
            {getStatusBadge(subscription.status, subscription.cancel_at_period_end)}
          </div>

          <div className="flex items-center text-sm text-persian-text-alt mb-4">
            <FaCalendar className="mr-2" />
            <span>
              {subscription.cancel_at_period_end
                ? `Access until ${formatDate(subscription.current_period_end)}`
                : `Next billing: ${formatDate(subscription.current_period_end)}`
              }
            </span>
          </div>

          <div className="flex gap-3">
            {subscription.status === 'active' && !subscription.cancel_at_period_end && (
              <button
                onClick={() => cancelSubscription(subscription.id)}
                disabled={canceling === subscription.id}
                className="flex items-center px-4 py-2 border border-persian-ruby text-persian-ruby rounded-lg hover:bg-persian-ruby/10 disabled:opacity-50"
              >
                {canceling === subscription.id ? (
                  <FaSpinner className="animate-spin mr-2" />
                ) : (
                  <FaTimes className="mr-2" />
                )}
                Cancel Subscription
              </button>
            )}

            {subscription.cancel_at_period_end && (
              <button
                onClick={() => reactivateSubscription(subscription.id)}
                disabled={reactivating === subscription.id}
                className="flex items-center px-4 py-2 bg-persian-sky text-persian-bg rounded-lg hover:bg-persian-turquoise disabled:opacity-50"
              >
                {reactivating === subscription.id ? (
                  <FaSpinner className="animate-spin mr-2" />
                ) : (
                  <FaCheck className="mr-2" />
                )}
                Reactivate Subscription
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SubscriptionManager; 